/**
 * OEM DATA-REQUEST EMAIL — turns an equipment envelope's open data ledger into
 * a ready-to-send RFI draft. This module only COMPOSES text: the user reviews
 * and sends the email from their own mail client (mailto:) or pastes the
 * copied draft. Nothing is ever sent automatically.
 *
 * Pure + deterministic: same context → same draft.
 */

export interface OemDataRequestContext {
  manufacturer: string;
  model: string;
  trainConfiguration: string;
  /** Project context the OEM needs to scope its response. */
  siteName: string;
  blockCount: number;
  /** Screening duty for context; omit when not yet solved. */
  targetNetMW?: number | null;
  /** The specific open items — catalog `unavailableFields` or the standard user-entry list. */
  requestedItems: readonly string[];
  /** Known OEM contact; empty string leaves the To: field for the user. */
  contactEmail?: string;
}

export interface OemDataRequestEmail {
  to: string;
  subject: string;
  body: string;
  /** `mailto:` URL opening the user's mail client with the draft prefilled. */
  mailtoHref: string;
}

/** Some mail clients truncate long mailto bodies; keep the draft comfortably under that. */
const MAILTO_BODY_LIMIT = 1_800;

export function buildOemDataRequestEmail(context: OemDataRequestContext): OemDataRequestEmail {
  const to = context.contactEmail?.trim() ?? '';
  const subject = `Technical data & availability request — ${context.manufacturer} ${context.model} (${context.trainConfiguration}) — ${context.siteName}`;

  const dutyLine = context.targetNetMW != null && Number.isFinite(context.targetNetMW)
    ? ` Our current screening basis is ${context.blockCount} × ${context.model} totalling approximately ${Math.round(context.targetNetMW)} MW net.`
    : ` Our current screening basis is ${context.blockCount} block(s) of ${context.model}.`;

  const requestLines = context.requestedItems
    .map((item, index) => `${index + 1}. ${item}`)
    .join('\n');

  const body = [
    `Dear ${context.manufacturer} team,`,
    '',
    `We are evaluating the ${context.model} (${context.trainConfiguration}) for on-site power generation at our ${context.siteName} project.${dutyLine}`,
    '',
    'To advance the engineering screen to a project basis, we request the following technical data:',
    '',
    requestLines,
    '',
    'We also request current production-slot availability, indicative lead time from order to delivery, and any slot-reservation framework you offer.',
    '',
    'We are happy to execute an NDA to receive project-grade documentation. Please direct the response, or an introduction to the appropriate regional application engineering contact, to this address.',
    '',
    'Kind regards,',
  ].join('\n');

  const truncatedBody = body.length > MAILTO_BODY_LIMIT
    ? `${body.slice(0, MAILTO_BODY_LIMIT - 1)}…`
    : body;
  const mailtoHref = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(truncatedBody)}`;

  return { to, subject, body, mailtoHref };
}
